import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { KapchargeConfigService } from '@lmc-app/core/services/kapcharge-config.service';
import { PaymentsCaConfigService } from '@lmc-app/core/services/payments-ca-config.service';
import { NgIf, NgFor } from '@angular/common';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import {
	MatCard,
	MatCardHeader,
	MatCardTitle,
	MatCardSubtitle,
	MatCardContent,
	MatCardActions,
} from '@angular/material/card';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';

interface GeneralModel {
	submitEnabled: boolean;
}

interface WebhookModel {
	webhookEnabled: boolean;
	webhookUrl: string;
	webhookAuthenticationHeader: string;
	subscribeStatuses: string[];
}

@Component({
	selector: 'app-payment-processing-settings',
	templateUrl: './payment-processing.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrls: ['./payment-processing.component.scss'],
	imports: [
		NgIf,
		MatProgressSpinner,
		ReactiveFormsModule,
		MatCard,
		MatCardHeader,
		MatCardTitle,
		MatCardSubtitle,
		MatCardContent,
		FormlyModule,
		MatCardActions,
		MatButton,
		NgFor,
		MatIcon,
	],
})
export class PaymentProcessingComponent implements OnInit {
	// --- Kapcharge: submission toggle ---
	// Merchant credentials and the API endpoint are sourced from the backend
	// (NLS-database.js), so the only editable general setting is the submit toggle.
	generalForm = new FormGroup({});
	generalModel: GeneralModel = {
		submitEnabled: false,
	};
	generalFields: FormlyFieldConfig[] = [
		{
			fieldGroupClassName: 'row',
			fieldGroup: [
				{
					className: 'col-12',
					key: 'submitEnabled',
					type: 'toggle',
					props: {
						label: 'Enable Submit',
						color: 'primary',
					},
				},
			],
		},
	];

	// --- Kapcharge: webhook ---
	webhookForm = new FormGroup({});
	webhookModel: WebhookModel = {
		webhookEnabled: false,
		webhookUrl: '',
		webhookAuthenticationHeader: '',
		subscribeStatuses: [],
	};
	webhookFields: FormlyFieldConfig[] = [
		{
			fieldGroupClassName: 'row',
			fieldGroup: [
				{
					className: 'col-12',
					key: 'webhookEnabled',
					type: 'toggle',
					props: {
						label: 'Enable Webhook',
						color: 'primary',
					},
				},
				{
					className: 'col-12 col-md-8 mt-3',
					key: 'webhookUrl',
					type: 'input',
					props: {
						label: 'Webhook URL',
						placeholder: 'https://www.example.com/webhook',
						appearance: 'outline',
						attributes: { autocomplete: 'off' },
					},
					expressionProperties: {
						'props.disabled': '!model.webhookEnabled',
					},
				},
				{
					className: 'col-12 col-md-8',
					key: 'webhookAuthenticationHeader',
					type: 'input',
					props: {
						label: 'Webhook Authentication Header',
						placeholder: 'e.g. Bearer your-secret-token',
						appearance: 'outline',
						attributes: { autocomplete: 'off' },
					},
					expressionProperties: {
						'props.disabled': '!model.webhookEnabled',
					},
				},
				{
					className: 'col-12',
					key: 'subscribeStatuses',
					type: 'multi-checkbox',
					props: {
						label: 'Subscribe Statuses',
						options: [
							{ label: 'Processing', value: 'Processing' },
							{ label: 'Accepted', value: 'Accepted' },
							{ label: 'Cancelled', value: 'Cancelled' },
							{ label: 'Returned', value: 'Returned' },
							{ label: 'TxnError', value: 'TxnError' },
						],
					},
					expressionProperties: {
						'props.disabled': '!model.webhookEnabled',
					},
				},
			],
		},
	];

	isLoading = false;
	isSavingGeneral = false;
	isSavingWebhook = false;

	// --- Payments Canada: FIF branch cache upload ---
	fifUploadFields = [
		{ key: 'banks', label: 'Banks' },
		{ key: 'trustCompanies', label: 'Trust Companies' },
		{ key: 'creditUnions', label: 'Credit Unions / Caisses Populaires' },
	];
	fifFiles: (File | null)[] = [null, null, null];
	isUploadingFif = false;

	constructor(
		private readonly kapchargeConfigService: KapchargeConfigService,
		private readonly paymentsCaConfigService: PaymentsCaConfigService,
		private readonly snackBar: MatSnackBar
	) {}

	ngOnInit(): void {
		this.loadConfig();
	}

	loadConfig(): void {
		this.isLoading = true;
		this.kapchargeConfigService.getConfig().subscribe({
			next: (res) => {
				const data = res.data || {};

				// Set isLoading = false first so *ngIf renders the formly-form components,
				// creating the form controls. Then update models on the next tick so Formly
				// can detect the reference change and patch the controls with DB values.
				this.isLoading = false;

				setTimeout(() => {
					this.generalModel = {
						submitEnabled: data.submitEnabled || false,
					};

					this.webhookModel = {
						webhookEnabled: data.webhookEnabled || false,
						webhookUrl: data.webhookUrl || '',
						webhookAuthenticationHeader: data.webhookAuthenticationHeader || '',
						subscribeStatuses: data.subscribeStatuses || [],
					};
				});
			},
			error: (err) => {
				console.error('[Kapcharge] Failed to load config', err);
				this.snackBar.open('Failed to load Kapcharge configuration', 'Close', {
					duration: 4000,
				});
				this.isLoading = false;
			},
		});
	}

	onSaveGeneral(): void {
		if (this.generalForm.invalid) return;

		this.isSavingGeneral = true;

		this.kapchargeConfigService
			.updateGeneralConfig({
				submitEnabled: this.generalModel.submitEnabled,
			})
			.subscribe({
				next: () => {
					this.snackBar.open('Submission setting saved successfully', 'Close', {
						duration: 4000,
					});
					this.isSavingGeneral = false;
				},
				error: (err) => {
					const msg = err.error?.message || 'Failed to save submission setting';
					this.snackBar.open(msg, 'Close', { duration: 5000 });
					this.isSavingGeneral = false;
				},
			});
	}

	onSaveWebhook(): void {
		if (this.webhookForm.invalid) return;

		this.isSavingWebhook = true;

		this.kapchargeConfigService
			.updateWebhookConfig({
				webhookEnabled: this.webhookModel.webhookEnabled,
				webhookUrl: this.webhookModel.webhookUrl || '',
				webhookAuthenticationHeader:
					this.webhookModel.webhookAuthenticationHeader || '',
				subscribeStatuses: this.webhookModel.subscribeStatuses || [],
			})
			.subscribe({
				next: () => {
					this.snackBar.open(
						'Webhook configuration saved successfully',
						'Close',
						{ duration: 4000 }
					);
					this.isSavingWebhook = false;
				},
				error: (err) => {
					const msg =
						err.error?.message || 'Failed to save webhook configuration';
					this.snackBar.open(msg, 'Close', { duration: 5000 });
					this.isSavingWebhook = false;
				},
			});
	}

	get canUploadFif(): boolean {
		return this.fifFiles.every((file) => file !== null) && !this.isUploadingFif;
	}

	onFifFileSelected(index: number, event: Event): void {
		const input = event.target as HTMLInputElement;
		const file = input.files && input.files.length ? input.files[0] : null;

		if (file && file.type !== 'application/pdf') {
			this.snackBar.open('Only PDF files are accepted', 'Close', {
				duration: 4000,
			});
			input.value = '';
			this.fifFiles[index] = null;
			return;
		}

		this.fifFiles[index] = file;
	}

	onUploadFif(): void {
		if (!this.canUploadFif) return;

		const files = this.fifFiles.filter((file): file is File => file !== null);
		this.isUploadingFif = true;

		this.paymentsCaConfigService.uploadFifBranchCache(files).subscribe({
			next: (res) => {
				const inserted = res?.data?.inserted ?? 0;
				this.snackBar.open(
					`FIF branch cache rebuilt: ${inserted} records inserted`,
					'Close',
					{
						duration: 5000,
					}
				);
				this.fifFiles = [null, null, null];
				this.isUploadingFif = false;
			},
			error: (err) => {
				const msg = err.error?.message || 'Failed to rebuild FIF branch cache';
				this.snackBar.open(msg, 'Close', { duration: 5000 });
				this.isUploadingFif = false;
			},
		});
	}
}
